import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { postToAuthEndpoint } from '../../config/axiosConfig';
import {
  clearStoredAuth,
  assertApiSuccess,
  normalizeUserRole,
  resolveSessionProfile,
  extractPayload,
  getErrorMessage,
  getStoredAuthToken,
  getStoredRefreshToken,
  getStoredUser,
  normalizeAuthResponse,
  storeAuthToken,
  storeRefreshToken,
  storeUser,
} from '../../utils/apiResponseUtils';

const persistAuthData = (authData) => {
  if (authData.token) {
    storeAuthToken(authData.token);
  }

  if (authData.refreshToken) {
    storeRefreshToken(authData.refreshToken);
  }
};

const hydrateVerificationProfile = async (authData) => {
  if (!authData.token || (typeof authData.user?.phoneVerificationRequired === 'boolean' &&
    typeof authData.user?.emailVerificationRequired === 'boolean')) return authData;
  persistAuthData(authData);
  try {
    const response = await axiosInstance.get('/auth/me', { skipSessionRefresh: true });
    const payload = extractPayload(response.data) || {};
    const profile = payload.user || payload.profile || payload.account || payload;
    return { ...authData, user: resolveSessionProfile({ ...(authData.user || {}), ...profile }, authData.user?.role) };
  } catch {
    // Profile availability must not reject otherwise valid login credentials.
    return authData;
  }
};

// Driver authentication never infers permission from the requested role.
const validateDriverSession = async (response, method, allowNoToken = false) => {
  try {
    assertApiSuccess(response);
    const authData = normalizeAuthResponse(response.data);
    if (!authData.token) {
      if (allowNoToken) return { ...authData, user: null, accountCreated: true };
      throw new Error('The server did not return an access token. Please sign in again.');
    }
    clearStoredAuth();
    persistAuthData(authData);
    const profileResponse = await axiosInstance.get('/auth/me');
    assertApiSuccess(profileResponse);
    const payload = extractPayload(profileResponse.data) || {};
    const user = payload.user || payload.profile || payload.driver || payload.account || payload;
    const driverProfile = resolveSessionProfile(user, 'driver');
    authData.user = { ...driverProfile, authMethod: method,
      ...(method === 'google' ? { provider: 'GOOGLE', emailVerified: true, emailVerificationRequired: false } : {}) };
    storeUser(authData.user);
    return authData;
  } catch (error) {
    clearStoredAuth();
    throw error;
  }
};

const clearBookingSessionData = () => {
  [
    'landingBookingFlow',
    'landingRouteDraft',
    'landingSelectedVehicle',
    'pendingTrackDeliveryId',
    'pendingTrackDeliveryIds',
  ].forEach((key) => sessionStorage.removeItem(key));
};

const PUBLIC_AUTH_RETRY_CONFIG = {
  retryAuthBaseOnStatuses: [404],
};

const getApiRole = (role) => {
  const normalizedRole = String(role || 'user').trim().toLowerCase();

  if (normalizedRole === 'driver') {
    return 'DRIVER';
  }

  if (normalizedRole === 'admin') {
    return 'admin';
  }

  return 'user';
};

const getPublicAuthErrorMessage = (error, fallbackMessage, blockedMessage = fallbackMessage) => {
  const status = error?.response?.status;
  const authBlockedMessage = status === 401 || status === 403
    ? blockedMessage
    : fallbackMessage;

  return getErrorMessage(error, authBlockedMessage, {
    ignoreMappedCodes: ['UNAUTHORIZED', 'FORBIDDEN'],
  });
};

// Async thunk for login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const requestedRole = credentials.role || 'user';
      if (String(requestedRole).toLowerCase() === 'driver') {
        const identifier = String(credentials.email || credentials.phone || credentials.username || '').trim();
        if (!identifier || String(credentials.password || '').length < 8) throw new Error('Enter an identifier and a password with at least eight characters.');
        const field = identifier.includes('@') ? 'email' : /^\d+$/.test(identifier) ? 'phone' : 'username';
        const response = await postToAuthEndpoint('/auth/login', { [field]: field === 'email' ? identifier.toLowerCase() : identifier, password: credentials.password, role: 'DRIVER' });
        return await validateDriverSession(response, 'email');
      }
      const loginCredentials = { ...credentials };
      if (loginCredentials.email) {
        const identifier = loginCredentials.email.trim();
        if (identifier.includes('@')) loginCredentials.email = identifier.toLowerCase();
        else {
          const field = requestedRole !== 'admin' && /^\d+$/.test(identifier) ? 'phone' : 'username';
          loginCredentials[field] = identifier;
          delete loginCredentials.email;
        }
      }
      const response = await postToAuthEndpoint('/auth/login', {
        ...loginCredentials,
        role: getApiRole(requestedRole),
      });
      const authData = await hydrateVerificationProfile(normalizeAuthResponse(response.data));
      if (credentials.role && authData.user) authData.user = resolveSessionProfile(authData.user, requestedRole);
      persistAuthData(authData);

      const nextUser = authData.user
        ? {
            ...authData.user,
            role: authData.user.role || requestedRole,
            authMethod: 'email',
          }
        : authData.token
          ? {
              username: credentials.username || '',
              email: credentials.email || '',
              role: requestedRole,
              authMethod: 'email',
            }
          : null;

      if (nextUser) {
        storeUser(nextUser);
      }

      return {
        ...authData,
        user: nextUser,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Login failed'));
    }
  }
);

// Async thunk for signup
export const signupUser = createAsyncThunk(
  'auth/signupUser',
  async (userData, { rejectWithValue }) => {
    try {
      const requestedRole = userData.role || 'user';
      if (String(requestedRole).toLowerCase() === 'driver') {
        const { firstName, lastName, email, phone, password, emailVerificationProofToken, phoneVerificationProofToken } = userData;
        if (!emailVerificationProofToken || !phoneVerificationProofToken) throw new Error('Verify both email and mobile before creating your account.');
        const response = await postToAuthEndpoint('/auth/signup', { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim().toLowerCase(), phone, password, role: 'DRIVER', emailVerificationProofToken, phoneVerificationProofToken });
        return await validateDriverSession(response, 'email', true);
      }
      const response = await postToAuthEndpoint('/auth/signup', {
        ...userData,
        role: getApiRole(requestedRole),
      });
      const authData = await hydrateVerificationProfile(normalizeAuthResponse(response.data));
      if (userData.role && authData.user) authData.user = resolveSessionProfile(authData.user, requestedRole);

      let nextUser = authData.user
        ? {
            ...authData.user,
            role: authData.user.role || requestedRole,
            authMethod: 'email',
          }
        : authData.token
          ? {
              email: userData.email || '',
              phone: userData.phone || '',
              role: requestedRole,
              authMethod: 'email',
            }
          : null;

      if (nextUser) nextUser = {
        ...nextUser,
        emailVerificationRequired: nextUser.emailVerificationRequired ?? (nextUser.emailVerified !== true),
        phoneVerificationRequired: nextUser.phoneVerificationRequired ?? (nextUser.phoneVerified !== true),
      };

      const nextAuthData = {
        ...authData,
        user: nextUser,
      };

      persistAuthData(nextAuthData);

      if (nextUser) {
        storeUser(nextUser);
      }

      return nextAuthData;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Signup failed'));
    }
  }
);

// Async thunk for social login (Google / Apple)
export const socialLogin = createAsyncThunk(
  'auth/socialLogin',
  async ({ provider, token: socialToken, role }, { rejectWithValue }) => {
    try {
      const response = await postToAuthEndpoint('/auth/social-login', {
        provider,
        token: socialToken,
        role: getApiRole(role),
      });
      if (String(role).toLowerCase() === 'driver') return await validateDriverSession(response, provider);
      assertApiSuccess(response);
      const authData = normalizeAuthResponse(response.data);
      if (!authData.token) throw new Error('The server did not return an access token. Please sign in again.');
      clearStoredAuth();
      persistAuthData(authData);
      const current = await axiosInstance.get('/auth/me');
      assertApiSuccess(current);
      const payload = extractPayload(current.data) || {};
      const profile = payload.user || payload.profile || payload.account || payload.customer || payload.driver || payload;
      if (!(profile.id || profile.role || profile.roles)) throw new Error('The server did not return a valid profile.');
      authData.user = { ...resolveSessionProfile(profile, authData.user?.role || role), authMethod: provider,
        ...(provider === 'google' ? { provider: 'GOOGLE', emailVerified: true, emailVerificationRequired: false } : {}) };
      storeUser(authData.user);

      return authData;
    } catch (error) {
      clearStoredAuth();
      return rejectWithValue(getErrorMessage(error, 'Social login failed'));
    }
  }
);

// Async thunk for requesting mobile OTP
export const requestMobileOtp = createAsyncThunk(
  'auth/requestMobileOtp',
  async ({ phone, purpose, role, firstName, lastName, email, vehicleNumber, licenseNumber }, { rejectWithValue }) => {
    try {
      const response = await postToAuthEndpoint(
        '/auth/send-otp',
        {
          phone,
          purpose,
          role: getApiRole(role),
          firstName,
          lastName,
          email,
          vehicleNumber,
          licenseNumber,
        },
        PUBLIC_AUTH_RETRY_CONFIG
      );

      assertApiSuccess(response, { delivery: 'sms' });
      const payload = extractPayload(response.data) || {};
      if (!payload.requestToken) throw new Error('The server did not return a verification request token.');

      return {
        ...payload,
        rawResponse: response.data,
        phone,
        purpose,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'OTP request failed'));
    }
  }
);

// Async thunk for verifying mobile OTP login/signup
export const verifyMobileOtp = createAsyncThunk(
  'auth/verifyMobileOtp',
  async ({ phone, otp, purpose, role, otpToken, requestToken, firstName, lastName, email, vehicleNumber, licenseNumber }, { rejectWithValue }) => {
    try {
      if (String(role).toLowerCase() === 'driver') {
        if (!requestToken || !/^\d{10}$/.test(phone) || !/^\d{6}$/.test(otp)) throw new Error('Request a mobile code and enter the six-digit OTP.');
        const response = await postToAuthEndpoint('/auth/verify-otp', { phone, requestToken, otp, purpose: 'LOGIN', role: 'DRIVER' });
        return await validateDriverSession(response, 'mobile');
      }
      const response = await postToAuthEndpoint(
        '/auth/verify-otp',
        {
          phone,
          otp,
          purpose,
          role: getApiRole(role),
          otpToken: otpToken || requestToken,
          requestToken: requestToken || otpToken,
          firstName,
          lastName,
          email,
          vehicleNumber,
          licenseNumber,
        },
        PUBLIC_AUTH_RETRY_CONFIG
      );

      const verifyPayload = extractPayload(response.data) || {};
      const authData = normalizeAuthResponse({
        ...response.data,
        ...verifyPayload,
        data: verifyPayload,
      });

      if (!authData.token) {
        return rejectWithValue(
          'OTP was verified, but /auth/verify-otp did not return a JWT in response.data.token.'
        );
      }

      const nextUser = authData.user
        ? {
            ...resolveSessionProfile(authData.user, role || 'user'),
            authMethod: 'mobile',
            phoneVerified: true,
            phoneVerificationRequired: authData.user.phoneVerificationRequired === true,
          }
        : authData.token
          ? {
              phone,
              role: role || 'user',
              authMethod: 'mobile',
              phoneVerified: true,
              phoneVerificationRequired: false,
            }
          : null;

      const nextAuthData = {
        ...authData,
        user: nextUser,
      };

      persistAuthData(nextAuthData);

      if (nextUser) {
        storeUser(nextUser);
      }

      return nextAuthData;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'OTP verification failed'));
    }
  }
);

// Prefer the latest stored JWT; Redux provides a fallback if storage is missing.
const getAccountVerificationConfig = (getState) => {
  const token = getStoredAuthToken() || getState().auth.token;
  if (!token) throw new Error('Your session has expired. Please sign in again.');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const validateAccountVerificationResponse = (response) => {
  assertApiSuccess(response, { delivery: true });
  if (response.data?.success === false) {
    const error = new Error(response.data.message || 'Phone verification failed.');
    error.response = response;
    throw error;
  }
};

const getConfirmedVerificationProfile = async (response, field, getState) => {
  const readProfile = (data) => {
    const payload = extractPayload(data) || {};
    return payload.user || payload.profile || payload.account || payload.customer || payload.driver || payload;
  };
  let profile = readProfile(response.data);
  const verified = field + 'Verified';
  const required = field + 'VerificationRequired';
  if (profile[verified] !== true || profile[required] === true) {
    const current = await axiosInstance.get('/auth/me', getAccountVerificationConfig(getState));
    validateAccountVerificationResponse(current);
    profile = readProfile(current.data);
  }
  if (profile[verified] !== true || profile[required] === true) {
    throw new Error(field === 'phone' ? 'Phone verification was not confirmed. Please try again.' : 'Email verification was not confirmed. Please try again.');
  }
  return profile;
};

// Account verification is authenticated and separate from OTP login/signup.
export const requestPhoneVerification = createAsyncThunk(
  'auth/requestPhoneVerification',
  async ({ phone }, { rejectWithValue, getState }) => {
    try {
      const response = await axiosInstance.post('/account/phone-verification/request', { phone }, getAccountVerificationConfig(getState));
      validateAccountVerificationResponse(response);
      const payload = extractPayload(response.data) || {};
      if (!(payload.requestToken || payload.otpToken)) throw new Error('The server did not return a verification request token. Please try again.');
      return { requestToken: payload.requestToken || payload.otpToken, phone };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, error.message || 'Failed to send OTP'));
    }
  }
);

export const confirmPhoneVerification = createAsyncThunk(
  'auth/confirmPhoneVerification',
  async ({ requestToken, otp, phone }, { rejectWithValue, getState }) => {
    try {
      const response = await axiosInstance.post('/account/phone-verification/confirm', { requestToken, otp }, getAccountVerificationConfig(getState));
      validateAccountVerificationResponse(response);
      const profile = await getConfirmedVerificationProfile(response, 'phone', getState);
      return { ...profile, phone: profile.phone || phone, phoneVerified: true, phoneVerificationRequired: false };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, error.message || 'Failed to verify OTP'));
    }
  }
);

export const requestEmailVerificationOtp = createAsyncThunk(
  'auth/requestEmailVerificationOtp',
  async ({ email }, { rejectWithValue, getState }) => {
    try {
      const response = await axiosInstance.post('/auth/email-verification/request', { email }, getAccountVerificationConfig(getState));
      validateAccountVerificationResponse(response);
      const payload = extractPayload(response.data) || {};
      const requestToken = payload.requestToken || payload.otpToken;
      if (!requestToken) throw new Error('The server did not return a verification request token. Please try again.');
      return { ...payload, requestToken, email };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, error.message || 'Failed to send email OTP'));
    }
  }
);

export const verifyEmailVerificationOtp = createAsyncThunk(
  'auth/verifyEmailVerificationOtp',
  async ({ email, otp, requestToken }, { rejectWithValue, getState }) => {
    try {
      const response = await axiosInstance.post('/auth/email-verification/confirm', { requestToken, otp, email }, getAccountVerificationConfig(getState));
      validateAccountVerificationResponse(response);
      const profile = await getConfirmedVerificationProfile(response, 'email', getState);
      const authData = normalizeAuthResponse(response.data);
      return { user: { ...profile, email: profile.email || email, emailVerified: true, emailVerificationRequired: false },
        token: authData.token, refreshToken: authData.refreshToken, email };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, error.message || 'Failed to verify email OTP'));
    }
  }
);

export const requestPasswordResetCode = createAsyncThunk(
  'auth/requestPasswordResetCode',
  async ({ email, phone }, { rejectWithValue }) => {
    try {
      const response = await postToAuthEndpoint(
        '/auth/password-reset/request',
        phone ? { phone } : { email },
        PUBLIC_AUTH_RETRY_CONFIG
      );
      assertApiSuccess(response, { delivery: true });
      const payload = extractPayload(response.data) || {};
      if (!payload.requestToken) throw new Error('The server did not return a reset request token.');

      return {
        ...payload,
        requestToken:
          payload.requestToken ||
          payload.request_token ||
          payload.otpToken ||
          payload.otp_token ||
          payload.requestId ||
          payload.sessionId ||
          null,
        message: payload.message || 'Reset OTP sent.',
      };
    } catch (error) {
      return rejectWithValue(
        getPublicAuthErrorMessage(
          error,
          'Failed to send reset OTP',
          'Password reset is temporarily unavailable. Please try again.'
        )
      );
    }
  }
);

export const resetPasswordWithCode = createAsyncThunk(
  'auth/resetPasswordWithCode',
  async ({ email, requestToken, otpToken, otp, code, newPassword }, { rejectWithValue }) => {
    try {
      const token = requestToken || otpToken;
      const response = await postToAuthEndpoint(
        '/auth/password-reset/confirm',
        {
          ...(email ? { email: email.trim().toLowerCase() } : {}),
          requestToken: token,
          otp: otp || code,
          newPassword,
        },
        PUBLIC_AUTH_RETRY_CONFIG
      );
      const payload = extractPayload(response.data) || {};

      return {
        ...payload,
        message: payload.message || 'Password reset successfully. You can log in now.',
      };
    } catch (error) {
      return rejectWithValue(
        getPublicAuthErrorMessage(
          error,
          'Failed to reset password',
          'Password reset is temporarily unavailable. Please try again.'
        )
      );
    }
  }
);

export const updateDriverProfile = createAsyncThunk('auth/updateDriverProfile', async ({ vehicleNumber, licenseNumber, vehicleType, capacityKg, dimensionsCm }, { getState, rejectWithValue }) => {
  try {
    const { user, isAuthenticated } = getState().auth;
    if (!isAuthenticated || normalizeUserRole(user) !== 'driver' || user.phoneVerified !== true) throw new Error('Verify your mobile number before saving driver details.');
    const details = { vehicleNumber: String(vehicleNumber || '').trim().toUpperCase(), licenseNumber: String(licenseNumber || '').trim().toUpperCase() };
    if (!details.vehicleNumber || !details.licenseNumber) throw new Error('Enter both vehicle and driving license numbers.');
    if (vehicleType !== undefined || capacityKg !== undefined || dimensionsCm !== undefined) {
      if (!['TRUCK','BIKE','SCOOTER','CAR'].includes(vehicleType) || ![capacityKg,dimensionsCm?.length,dimensionsCm?.width,dimensionsCm?.height].every(value=>typeof value==='number' && Number.isFinite(value) && value>0)) throw new Error('Enter a vehicle type, positive cargo capacity and dimensions.');
      Object.assign(details,{vehicleType,capacityKg,dimensionsCm});
    }
    const config = getAccountVerificationConfig(getState);
    const response = await axiosInstance.patch('/auth/me/driver-profile', details, config);
    assertApiSuccess(response);
    const read = data => { const payload = extractPayload(data) || {}; return payload.user || payload.profile || payload.driver || payload.account || payload; };
    let profile = read(response.data);
    const saved = value => value?.vehicleNumber?.trim().toUpperCase() === details.vehicleNumber && value?.licenseNumber?.trim().toUpperCase() === details.licenseNumber;
    if (!saved(profile)) { const current = await axiosInstance.get('/auth/me', config); assertApiSuccess(current); profile = read(current.data); }
    if (!saved(profile)) throw new Error('The server did not confirm the saved vehicle and driving license details. Please retry.');
    return profile;
  } catch (error) { return rejectWithValue(getErrorMessage(error, 'Could not save driver details.')); }
});

export const refreshCurrentUser = createAsyncThunk('auth/refreshCurrentUser', async (_, { rejectWithValue }) => {
  try {
    if (!getStoredAuthToken()) throw new Error('No active session found');
    const response = await axiosInstance.get('/auth/me');
    assertApiSuccess(response);
    const payload = extractPayload(response.data) || {};
    const user = payload.user || payload.profile || payload.driver || payload.account || payload;
    if (!user || !Object.keys(user).length || !(user.id || user.email || user.role || user.roles)) throw new Error('The server did not return a valid profile.');
    return { token: getStoredAuthToken(), refreshToken: getStoredRefreshToken(), user: resolveSessionProfile(user, getStoredUser()?.role) };
  } catch (error) {
    clearStoredAuth();
    return rejectWithValue(getErrorMessage(error, error.message || 'Session expired'));
  }
});

export const logoutUser = createAsyncThunk('auth/logoutUser', async () => {
  try { await axiosInstance.post('/auth/logout'); } catch { /* Local logout remains available offline. */ }
  clearStoredAuth();
  clearBookingSessionData();
  sessionStorage.removeItem('driver_completed_orders_v1');
  return true;
});

const initialState = {
  user: getStoredUser(),
  token: getStoredAuthToken(),
  refreshToken: getStoredRefreshToken(),
  loading: false,
  error: null,
  isAuthenticated: !!getStoredAuthToken(),
  otpRequested: false,
  otpRequestMeta: null,
  sessionRefreshing: false,
  restoring: !!getStoredAuthToken(),
  expiresIn: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.restoring = false; state.loading = false; state.error = null; state.expiresIn = null;
      state.otpRequested = false;
      state.otpRequestMeta = null;
      clearStoredAuth();
      clearBookingSessionData();
    },
    updateUserProfile: (state, action) => {
      state.user = { ...(state.user || {}), ...action.payload, role: state.user?.role || action.payload.role };
      storeUser(state.user);
    },
    selectAccountRole: (state, action) => {
      state.user = resolveSessionProfile(state.user, action.payload);
      storeUser(state.user);
    },
    requireEmailVerification: (state) => {
      state.user = { ...(state.user || {}), emailVerified: false, emailVerificationRequired: true };
      storeUser(state.user);
    },
    requirePhoneVerification: (state) => {
      state.user = { ...(state.user || {}), phoneVerified: false, phoneVerificationRequired: true };
      storeUser(state.user);
    },
    clearError: (state) => {
      state.error = null;
    },
    clearOtpState: (state) => {
      state.otpRequested = false;
      state.otpRequestMeta = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(confirmPhoneVerification.fulfilled, (state, action) => {
        state.user = { ...(state.user || {}), ...action.payload, role: state.user?.role || action.payload.role, phoneVerified: true, phoneVerificationRequired: false };
        storeUser(state.user);
      })
      .addCase(updateDriverProfile.fulfilled, (state, action) => {
        state.user = { ...state.user, ...action.payload };
        storeUser(state.user);
      })
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.expiresIn = action.payload.expiresIn || null;
        state.refreshToken = action.payload.refreshToken || null;
        state.isAuthenticated = !!action.payload.token;
        if (!action.payload.token) {
          state.error = 'Authentication succeeded but no token was returned by the backend.';
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (!getStoredAuthToken()) { state.isAuthenticated = false; state.user = null; state.token = null; state.refreshToken = null; }
      })
      // Signup cases
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken || null;
        state.isAuthenticated = !!action.payload.token;
        if (!action.payload.token) {
          state.error = null;
        }
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (!getStoredAuthToken()) { state.isAuthenticated = false; state.user = null; state.token = null; state.refreshToken = null; }
      })
      // Social login cases
      .addCase(socialLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(socialLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken || null;
        state.isAuthenticated = !!action.payload.token;
        if (!action.payload.token) {
          state.error = 'Social login succeeded but no token was returned by the backend.';
        }
      })
      .addCase(socialLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (!getStoredAuthToken()) { state.isAuthenticated = false; state.user = null; state.token = null; state.refreshToken = null; }
      })
      // Request OTP cases
      .addCase(requestMobileOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestMobileOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.otpRequested = true;
        state.otpRequestMeta = {
          phone: action.payload.phone,
          purpose: action.payload.purpose,
          requestToken:
            action.payload.requestToken ||
            action.payload.request_token ||
            action.payload.otpToken ||
            action.payload.otp_token ||
            action.payload.requestId ||
            action.payload.sessionId ||
            null,
          otpToken:
            action.payload.otpToken ||
            action.payload.otp_token ||
            action.payload.requestToken ||
            action.payload.request_token ||
            action.payload.requestId ||
            action.payload.sessionId ||
            null,
          expiresIn: action.payload.expiresIn || null,
          message: action.payload.message || 'OTP sent successfully',
        };
      })
      .addCase(requestMobileOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (!getStoredAuthToken()) { state.isAuthenticated = false; state.user = null; state.token = null; state.refreshToken = null; }
        state.otpRequested = false;
        state.otpRequestMeta = null;
      })
      // Verify OTP cases
      .addCase(verifyMobileOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyMobileOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken || null;
        state.isAuthenticated = !!action.payload.token;
        state.otpRequested = false;
        state.otpRequestMeta = null;
        if (!action.payload.token) {
          state.error = 'OTP verification succeeded but no token was returned by the backend.';
        }
      })
      .addCase(verifyMobileOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (!getStoredAuthToken()) { state.isAuthenticated = false; state.user = null; state.token = null; state.refreshToken = null; }
      })
      .addCase(requestEmailVerificationOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestEmailVerificationOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(requestEmailVerificationOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (!getStoredAuthToken()) { state.isAuthenticated = false; state.user = null; state.token = null; state.refreshToken = null; }
      })
      .addCase(verifyEmailVerificationOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmailVerificationOtp.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.token) {
          state.token = action.payload.token;
          state.isAuthenticated = true;
          storeAuthToken(action.payload.token);
        }

        if (action.payload.refreshToken) {
          state.refreshToken = action.payload.refreshToken;
          storeRefreshToken(action.payload.refreshToken);
        }

        state.user = state.user
          ? {
              ...state.user,
              ...(action.payload.user || {}),
              emailVerified: true,
              emailVerificationRequired: false,
            }
          : action.payload.user || null;

        if (state.user) {
          storeUser(state.user);
        }
      })
      .addCase(verifyEmailVerificationOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (!getStoredAuthToken()) { state.isAuthenticated = false; state.user = null; state.token = null; state.refreshToken = null; }
      })
      .addCase(requestPasswordResetCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestPasswordResetCode.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(requestPasswordResetCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (!getStoredAuthToken()) { state.isAuthenticated = false; state.user = null; state.token = null; state.refreshToken = null; }
      })
      .addCase(resetPasswordWithCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPasswordWithCode.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resetPasswordWithCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (!getStoredAuthToken()) { state.isAuthenticated = false; state.user = null; state.token = null; state.refreshToken = null; }
      })
      // Refresh current user/session
      .addCase(refreshCurrentUser.pending, (state) => {
        state.sessionRefreshing = true;
      })
      .addCase(refreshCurrentUser.fulfilled, (state, action) => {
        state.sessionRefreshing = false;
        state.restoring = false;

        if (action.payload.token) {
          state.token = action.payload.token;
          state.isAuthenticated = true;
        }

        if (action.payload.refreshToken) {
          state.refreshToken = action.payload.refreshToken;
        }

        if (action.payload.user) {
          state.user = {
            ...(state.user || {}),
            ...action.payload.user,
            role: action.payload.user.role || state.user?.role,
          };
          storeUser(state.user);
        }
      })
      .addCase(refreshCurrentUser.rejected, (state) => {
        state.sessionRefreshing = false; state.restoring = false;
        state.user = null; state.token = null; state.refreshToken = null; state.isAuthenticated = false;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.otpRequested = false;
        state.otpRequestMeta = null;
      })
      .addCase(logoutUser.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.otpRequested = false;
        state.otpRequestMeta = null;
      });
  },
});

export const { logout, clearError, clearOtpState, updateUserProfile, selectAccountRole } = authSlice.actions;
export default authSlice.reducer;
